import { useState } from 'react'
import { router } from '@inertiajs/react'
import type { AgendaTemplateSection } from '../../types'

interface Props {
  templateId: number
  existingSections: AgendaTemplateSection[]
  onClose: () => void
}

const STANDARD_SECTIONS = [
  { type: 'set_the_stage',        title: 'Set the Stage',           duration: 5  },
  { type: 'check_in',             title: 'Check-In',                duration: 5  },
  { type: 'scoreboard',           title: 'Scoreboard',              duration: 10 },
  { type: 'rock_review',          title: 'Rock Review',             duration: 5  },
  { type: 'action_items',         title: 'Accountability from Last Week', duration: 5  },
  { type: 'announcements',        title: 'Team Engagement',         duration: 5  },
  { type: 'business_development', title: 'Business Development',    duration: 5  },
  { type: 'topics',               title: 'The List',                duration: 40 },
  { type: 'check_out',            title: 'Check-Out',               duration: 5  },
]

export default function AddCustomSectionModal({ templateId, existingSections, onClose }: Props) {
  const [mode, setMode] = useState<'pick' | 'custom' | 'heading'>('pick')
  const [customTitle, setCustomTitle] = useState('')
  const [customDuration, setCustomDuration] = useState(5)
  const [customNotes, setCustomNotes] = useState('')
  const [processing, setProcessing] = useState(false)

  const handleAddHeading = (e: React.FormEvent) => {
    e.preventDefault()
    if (!customTitle.trim()) return
    setProcessing(true)
    router.post(`/agenda_templates/${templateId}/agenda_template_sections`, {
      agenda_template_section: {
        section_type: 'custom',
        title: customTitle.trim(),
        is_heading: true,
      }
    }, {
      onSuccess: onClose,
      onFinish: () => setProcessing(false),
      preserveScroll: true
    })
  }

  const existingTypes = existingSections.map(s => s.section_type)
  const availableStandard = STANDARD_SECTIONS.filter(s => !existingTypes.includes(s.type))

  const handleAddStandard = (section: typeof STANDARD_SECTIONS[0]) => {
    setProcessing(true)
    router.post(`/agenda_templates/${templateId}/agenda_template_sections`, {
      agenda_template_section: {
        section_type: section.type,
        duration_minutes: section.duration,
      }
    }, {
      onSuccess: onClose,
      onFinish: () => setProcessing(false),
      preserveScroll: true
    })
  }

  const handleAddCustom = (e: React.FormEvent) => {
    e.preventDefault()
    if (!customTitle.trim()) return
    setProcessing(true)
    router.post(`/agenda_templates/${templateId}/agenda_template_sections`, {
      agenda_template_section: {
        section_type: 'custom',
        title: customTitle.trim(),
        duration_minutes: customDuration,
        custom_notes: customNotes,
      }
    }, {
      onSuccess: onClose,
      onFinish: () => setProcessing(false),
      preserveScroll: true
    })
  }

  return (
    <div className="fixed inset-0 bg-charcoal/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone">
          <h2 className="text-base font-semibold text-text-primary">
            {mode === 'pick' ? 'Add Section' : mode === 'heading' ? 'Add Heading' : 'Add Custom Section'}
          </h2>
          <button onClick={onClose} className="opacity-40 hover:opacity-70">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {mode === 'pick' ? (
          <div className="px-6 py-4">
            {availableStandard.length > 0 && (
              <>
                <p className="text-xs font-bold uppercase tracking-widest text-text-muted mb-3">Standard Sections</p>
                <div className="space-y-1 mb-4">
                  {availableStandard.map(section => (
                    <button
                      key={section.type}
                      onClick={() => handleAddStandard(section)}
                      disabled={processing}
                      className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors text-left disabled:opacity-50"
                    >
                      <span className="text-sm font-medium text-text-primary">{section.title}</span>
                      <span className="text-xs text-text-muted">{section.duration}m</span>
                    </button>
                  ))}
                </div>
              </>
            )}

            {availableStandard.length === 0 && (
              <p className="text-sm text-text-muted mb-4">All standard sections are already in this agenda.</p>
            )}

            <div className="border-t border-stone pt-4 space-y-1">
              <button
                onClick={() => setMode('custom')}
                className="flex items-center gap-2 w-full px-3 py-2.5 rounded-lg hover:bg-charcoal/5 transition-colors text-left"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-text-primary">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                <span className="text-sm font-semibold text-text-primary">Create Custom Section</span>
              </button>
              <button
                onClick={() => setMode('heading')}
                className="flex items-center gap-2 w-full px-3 py-2.5 rounded-lg hover:bg-charcoal/5 transition-colors text-left"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-text-primary">
                  <path d="M6 4v16"/><path d="M18 4v16"/><path d="M6 12h12"/>
                </svg>
                <span className="text-sm font-semibold text-text-primary">Add Heading</span>
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={mode === 'heading' ? handleAddHeading : handleAddCustom}>
            <div className="px-6 py-5 space-y-4">
              <div>
                <button
                  type="button"
                  onClick={() => setMode('pick')}
                  className="text-xs text-text-primary hover:underline mb-3 flex items-center gap-1"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6"/>
                  </svg>
                  Back
                </button>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-text-primary mb-2">
                  {mode === 'heading' ? 'Heading Label *' : 'Section Title *'}
                </label>
                <input
                  type="text"
                  value={customTitle}
                  onChange={e => setCustomTitle(e.target.value)}
                  placeholder={mode === 'heading' ? 'e.g. Review Prior Month Financials' : 'e.g. Department Updates'}
                  className="w-full px-3 py-2 border border-stone rounded text-sm focus:border-charcoal focus:outline-none"
                  autoFocus
                />
                {mode === 'heading' && (
                  <p className="mt-2 text-xs text-text-muted">
                    Headings are visual labels — they have no timer and won't be advanced into during the meeting.
                  </p>
                )}
              </div>
              {mode !== 'heading' && (
                <>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-text-primary mb-2">Duration (minutes)</label>
                    <input
                      type="number"
                      min={1}
                      max={60}
                      value={customDuration}
                      onChange={e => setCustomDuration(parseInt(e.target.value) || 5)}
                      className="w-24 px-3 py-2 border border-stone rounded text-sm focus:border-charcoal focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-text-primary mb-2">Notes (optional)</label>
                    <textarea
                      value={customNotes}
                      onChange={e => setCustomNotes(e.target.value)}
                      placeholder="What should be covered?"
                      rows={3}
                      className="w-full px-3 py-2 border border-stone rounded text-sm focus:border-charcoal focus:outline-none resize-none"
                    />
                  </div>
                </>
              )}
            </div>
            <div className="px-6 py-4 border-t border-stone flex justify-end gap-3">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-text-primary bg-white border border-stone rounded hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button
                type="submit"
                disabled={processing || !customTitle.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-pinnacle-red rounded hover:bg-pinnacle-red-dark transition-colors disabled:opacity-40"
              >
                {processing ? 'Adding...' : 'Add Section'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
